import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ProgressCircle } from '@adobe/react-spectrum';
import { searchProvider } from '../../hooks/searchContext';
import actionWebInvoke from '../../utils/actionCallUtils';
import getHeaders from '../../utils/headers';
import actions from '../../config.json';
import SearchResult from './SearchResult';
import SearchBoxStyle from './Style';

const Search = props => {
  const { ims } = props;
  const [state, setState] = useState();
  const [count, setCount] = useState(30);
  const [bookmarkedImages, setBookMarked] = useState([]);
  const userID = ims.profile.userId;

  const {
    stockResult,
    aemResult,
    ccResult,
    projectResult,
    taskResult,
    status,
    isLoading,
  } = searchProvider();
  const { stockImageResponse = [], stockSearchText } = stockResult;
  const { aemImageResponse = [], aemSearchText } = aemResult;
  const { ccImageResponse = [], ccSearchText } = ccResult;
  const { projectResponse = [], projectSearchText } = projectResult;
  const { taskResponse = [], taskSearchText } = taskResult;

  const getSearchResult = () => {
    if (status === 'stockImage')
      return {
        searchText: stockSearchText,
        searchImage: stockImageResponse,
        imageSource: 'Adobe Stock',
      };
    if (status === 'aemImage')
      return {
        searchText: aemSearchText,
        searchImage: aemImageResponse,
        imageSource: 'AEM Assets',
      };
    if (status === 'ccImage')
      return {
        searchText: ccSearchText,
        searchImage: ccImageResponse,
        imageSource: 'CC Libraries',
      };
    if (status === 'projectsSearch')
      return {
        searchText: projectSearchText,
        searchResult: projectResponse,
        imageSource: 'Projects',
      };
    if (status === 'tasksSearch')
      return {
        searchText: taskSearchText,
        searchResult: taskResponse,
        imageSource: 'Tasks',
      };
    return [];
  };

  async function getUserBoards() {
    const getBoard = true;
    const headers = getHeaders(ims);
    const response = await actionWebInvoke(
      actions['bookmark-action'],
      headers,
      {
        userID,
        getBoard,
      },
    );

    setState({ ...state, boards: response });
  }

  async function createUserBoards(boardname) {
    const setBoard = true;
    const headers = getHeaders(ims);
    await actionWebInvoke(actions['bookmark-action'], headers, {
      setBoard,
      userID,
      boardname,
    });
  }

  async function getAllBoardImages() {
    const getBoardImages = true;
    const headers = getHeaders(ims);
    const response = await actionWebInvoke(
      actions['bookmark-action'],
      headers,
      {
        getBoardImages,
        userID,
      },
    );
    const bookmarkImages = new Set([...bookmarkedImages, ...response]);
    setBookMarked([...bookmarkImages]);
  }

  async function setImageToBoard(imageURL, imageID, imageName, board) {
    const setImage = true;
    const headers = getHeaders(ims);
    await actionWebInvoke(actions['bookmark-action'], headers, {
      userID,
      setImage,
      imageURL,
      imageID,
      imageName,
      board,
    });
    setBookMarked([...bookmarkedImages, imageName]);
  }

  const handleLoadMore = data => {
    const limit = 30;
    if (count < data.length) setCount(count + limit);
  };

  useEffect(() => {
    getAllBoardImages();
    getUserBoards();
  }, [ims]);

  useEffect(() => {
    if (
      stockImageResponse.length ||
      aemImageResponse.length ||
      ccImageResponse.length ||
      projectResponse.length ||
      taskResponse.length
    ) {
      setCount(30);
    }
  }, [
    JSON.stringify(stockResult),
    JSON.stringify(aemResult),
    JSON.stringify(ccResult),
    JSON.stringify(projectResult),
    JSON.stringify(taskResult),
  ]);

  return (
    <SearchBoxStyle>
      <div className="flexContainer">
        {isLoading ? (
          <div className="loader">
            <ProgressCircle
              aria-label="Loading…"
              size="L"
              isIndeterminate={isLoading}
            />
          </div>
        ) : (
          <SearchResult
            data={getSearchResult()}
            isAdobeStock={status === 'stockImage'}
            isProjectSearch={status === 'projectsSearch'}
            isTaskSearch={status === 'tasksSearch'}
            handleLoadMore={handleLoadMore}
            {...{
              isLoading,
              count,
              createUserBoards,
              getUserBoards,
              state,
              setImageToBoard,
              bookmarkedImages,
            }}
          />
        )}
      </div>
    </SearchBoxStyle>
  );
};

Search.propTypes = {
  ims: PropTypes.shape({
    org: PropTypes.string,
    token: PropTypes.string,
    profile: PropTypes.shape({
      userId: PropTypes.string,
    }),
  }).isRequired,
};

export default Search;
